// Small formatting helpers shared by pages.

/**
 * Display label for a work item identifier.
 *
 * ## 0.1.48 policy (dr.the0, 2026-07-04) — key is primary
 *
 * The canonical Tasko `key` (e.g. `CTSK-156`) is ALWAYS the primary
 * label. The importer-supplied `externalId` (e.g. `ctsk-038` for a
 * pre-Tasko YAML round-trip identifier) is:
 *
 *   - Rendered as a secondary badge **when** it exists AND differs
 *     from `key.toLowerCase()` — i.e. it carries information beyond
 *     the Tasko key.
 *   - Suppressed entirely when it's absent OR when it matches the
 *     lowercase key (which is a common "same identifier in both
 *     fields" pattern for newly-filed items).
 *
 * ### Why the flip vs prior behavior?
 *
 * Earlier behavior promoted `externalId` to primary and demoted `key`
 * to secondary chrome — that was helpful when the importer's ID was
 * the identifier operators recognized (`coo-027`). But it drifted for
 * newer tickets where the parent-lineage naming diverged from the
 * server-assigned key. Concrete example that surfaced the pain:
 * `CTSK-156` was filed with `externalId: 'ctsk-038a'` (parent lineage
 * of the `ctsk-038` epic) — the UI showed `ctsk-038a` everywhere while
 * the API returned `CTSK-156`, and cross-references (comments,
 * tasko-mcp responses, docs) drifted between the two surfaces.
 *
 * Flipping to "key leads, externalId is a badge only when meaningfully
 * different" makes the canonical identifier the operator always sees
 * FIRST, while preserving the historic identifier trail for items that
 * had a legitimate pre-Tasko id.
 *
 * ### Returns
 *
 *   - `primary`: what to lead with — always `key`.
 *   - `secondary`: optional side badge — `externalId` when it differs
 *     from `key.toLowerCase()`, else `null`.
 *   - `combined`: compact single-string form (`"CTSK-46"` or
 *     `"CTSK-46 · ctsk-038"`).
 *
 * See `feedback_workitem_external_id_hygiene` (session memory) for
 * the going-forward writer-side rule.
 */
export interface WorkItemKeyDisplay {
  primary: string;
  secondary: string | null;
  combined: string;
}
export function formatWorkItemKey(item: {
  key: string;
  externalId?: string | null;
}): WorkItemKeyDisplay {
  const ext = item.externalId?.trim() ?? '';
  const keyLower = item.key.toLowerCase();
  // Show externalId as a badge only when it exists AND materially
  // differs from the lowercase key. `ctsk-156` next to `CTSK-156` is
  // just noise; `ctsk-038` next to `CTSK-46` is information.
  if (ext.length > 0 && ext.toLowerCase() !== keyLower) {
    return {
      primary: item.key,
      secondary: ext,
      combined: `${item.key} · ${ext}`,
    };
  }
  return {
    primary: item.key,
    secondary: null,
    combined: item.key,
  };
}

export function formatBytes(bytes: number | undefined | null): string {
  if (!bytes || bytes < 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

export function formatNumber(n: number | undefined | null): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat().format(n);
}

/**
 * Days a row is past its due date. Returns 0 if not past-due (either
 * the API hasn't flagged isPastDue, or dueDate is null). Otherwise
 * returns a positive integer count of full days elapsed since dueDate.
 *
 * Floors to whole days — "1 day overdue" means crossed midnight at
 * least once. A row due today but past its dueDate clock-time would
 * return 0; the API decides what "past due" means structurally, this
 * helper just renders the human-friendly delta.
 *
 * Per docs/future-scope.md §6 — purely client-side derive from
 * existing isPastDue + dueDate. No schema change.
 */
export function daysPastDue(
  isPastDue: boolean | undefined,
  dueDate: string | null | undefined,
  now: Date = new Date(),
): number {
  if (!isPastDue || !dueDate) return 0;
  const due = new Date(dueDate);
  if (Number.isNaN(due.getTime())) return 0;
  const ms = now.getTime() - due.getTime();
  if (ms <= 0) return 0;
  return Math.floor(ms / 86_400_000);
}

/** "3d overdue" / "1d overdue" / empty string when n=0. Short form
 *  for table cells where space is tight. */
export function formatDaysPastDueShort(days: number): string {
  if (days <= 0) return '';
  return `${days}d overdue`;
}

/** "3 days overdue" / "1 day overdue" / empty when n=0. Long form
 *  for detail-header chips. */
export function formatDaysPastDueLong(days: number): string {
  if (days <= 0) return '';
  return days === 1 ? '1 day overdue' : `${days} days overdue`;
}

/**
 * Human-friendly label for the MIME, paired with the MimeIcon
 * component (components/MimeIcon.tsx) for hover tooltips on the icon.
 * Returns the raw mime string when no friendly label is known —
 * better than guessing wrong.
 *
 * (The emoji-based iconForMime() helper that previously lived here
 * was replaced by the Lucide-backed <MimeIcon> component in the
 * Documents list refresh — keep this label helper since icons are
 * decorative without a paired hover-text.)
 */
export function iconLabelForMime(mime: string | null | undefined): string {
  const m = (mime ?? '').toLowerCase();
  if (!m) return 'Unknown type';
  if (m.startsWith('image/')) return `Image (${m.slice(6)})`;
  if (m.startsWith('video/')) return `Video (${m.slice(6)})`;
  if (m.startsWith('audio/')) return `Audio (${m.slice(6)})`;
  switch (m) {
    case 'application/pdf': return 'PDF';
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    case 'application/msword': return 'Word document';
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    case 'application/vnd.ms-excel': return 'Excel spreadsheet';
    case 'text/csv': return 'CSV';
    case 'text/tab-separated-values': return 'TSV';
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    case 'application/vnd.ms-powerpoint': return 'PowerPoint';
    case 'application/json': return 'JSON';
    case 'application/yaml':
    case 'application/x-yaml':
    case 'text/yaml': return 'YAML';
    case 'application/xml':
    case 'text/xml': return 'XML';
    case 'text/html': return 'HTML';
    case 'application/javascript': return 'JavaScript';
    case 'application/typescript': return 'TypeScript';
    case 'application/x-python': return 'Python';
    case 'application/x-sh':
    case 'application/x-shellscript': return 'Shell script';
    case 'text/markdown': return 'Markdown';
    case 'text/plain': return 'Plain text';
    case 'application/x-log': return 'Log file';
    case 'application/zip': return 'ZIP archive';
    case 'application/x-tar': return 'tar archive';
    case 'application/gzip': return 'gzip';
    default: return m;
  }
}
